"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Heart, Target, Users, Rocket, Sparkles, ArrowLeft } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import PageSkeleton from "@/components/site/PageSkeleton";

const VALUES = [
  {
    icon: Heart,
    title: "مشتری در قلب همه‌چیز",
    desc: "هر تصمیم محصول از یک سؤال شروع می‌شود: این چه دردی را از کاربر کم می‌کند؟",
  },
  {
    icon: Target,
    title: "تمرکز بر تأثیر",
    desc: "روی چیزهایی کار می‌کنیم که نتیجه قابل اندازه‌گیری برای کسب‌وکار شما داشته باشد.",
  },
  {
    icon: Sparkles,
    title: "سادگی شجاعانه",
    desc: "ساده‌سازی سخت‌تر از پیچیده‌کردن است. ما مسیر سخت را انتخاب می‌کنیم.",
  },
  {
    icon: Rocket,
    title: "سرعت یادگیری",
    desc: "هر هفته منتشر می‌کنیم، می‌سنجیم و یاد می‌گیریم. هیچ‌چیز نهایی نیست.",
  },
];

const TEAM = [
  {
    name: "علی محمدی",
    role: "بنیان‌گذار و مدیرعامل",
    initials: "ع.م",
    color: "from-primary-main to-primary-300",
  },
  {
    name: "سارا حسینی",
    role: "مدیر محصول",
    initials: "س.ح",
    color: "from-secondary-main to-secondary-300",
  },
  { name: "نیما رضایی", role: "سرپرست مهندسی", initials: "ن.ر", color: "from-purple to-blue" },
  { name: "مریم کاظمی", role: "مدیر طراحی", initials: "م.ک", color: "from-teal to-light-blue" },
];

const MILESTONES = [
  {
    year: "۱۴۰۲",
    title: "آغاز سفر",
    desc: "ایده در یک کافه شکل گرفت و اولین نمونه اولیه ساخته شد.",
  },
  { year: "۱۴۰۲", title: "اولین ۱۰۰ کاربر", desc: "صد فروشگاه پیشگام به ما اعتماد کردند." },
  { year: "۱۴۰۳", title: "هوش مصنوعی فارسی", desc: "اولین مدل پاسخگویی فارسی اختصاصی منتشر شد." },
  {
    year: "۱۴۰۴",
    title: "بیش از ۵٬۰۰۰ کسب‌وکار",
    desc: "خانوادهٔ ترندی اسمارت به ۵ هزار برند رسید.",
  },
];

export default function AboutPage() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 900);
    return () => clearTimeout(t);
  }, []);
  if (!ready) return <PageSkeleton variant="team" />;

  return (
    <SiteShell>
      {/* Hero */}
      <section className="mx-auto max-w-5xl px-6 pt-16 pb-12 sm:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
            <Heart size={14} className="text-secondary-main" />
            <span className="text-[12px] font-bold text-muted-foreground">قصه ما</span>
          </div>
          <h1 className="mb-5 text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
            ساخته شده برای
            <span className="block brand-gradient-text">کسب‌وکارهای ایرانی</span>
          </h1>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            ما باور داریم هر کارآفرین ایرانی شایستهٔ ابزارهای کلاس جهانی است — بومی، ساده و قدرتمند.
            این چیزی است که هر روز برایش بیدار می‌شویم.
          </p>
        </motion.div>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-4xl px-6 pb-16">
        <div className="rounded-2xl border border-border bg-card p-8 sm:p-10">
          <h2 className="mb-4 text-2xl font-black tracking-tight text-foreground">داستان ما</h2>
          <div className="space-y-4 text-[14px] leading-loose text-muted-foreground">
            <p>
              همه‌چیز از یک ناامیدی ساده شروع شد: چرا یک فروشگاه کوچک ایرانی باید بین ۱۰ ابزار
              متفاوت پاره‌پاره شود؟ چرا ابزارهای خارجی هیچ‌وقت زبان ما را درست نمی‌فهمند؟
            </p>
            <p>
              تصمیم گرفتیم پلتفرمی بسازیم که از روز اول برای فارسی، برای فرهنگ ما و برای واقعیت‌های
              اقتصادی ایران طراحی شده باشد — نه ترجمه‌ای از یک محصول دیگر.
            </p>
            <p>
              امروز هزاران کسب‌وکار از ترندی اسمارت برای رشد، اتوماسیون و فهمیدن مشتریان‌شان استفاده
              می‌کنند. این فقط آغاز ماجراست.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <h2 className="mb-8 text-center text-2xl font-black tracking-tight text-foreground sm:text-3xl">
          ارزش‌هایی که ما را هدایت می‌کنند
        </h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary-main/40"
              >
                <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-main/15 text-primary-main">
                  <Icon size={18} />
                </div>
                <h3 className="mb-2 text-[15px] font-black text-foreground">{v.title}</h3>
                <p className="text-[12.5px] leading-relaxed text-muted-foreground">{v.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Timeline */}
      <section className="mx-auto max-w-4xl px-6 pb-16">
        <h2 className="mb-8 text-center text-2xl font-black tracking-tight text-foreground sm:text-3xl">
          مسیری که آمده‌ایم
        </h2>
        <div className="relative">
          <div className="absolute right-4 top-2 bottom-2 w-px bg-gradient-to-b from-primary-main/40 via-border to-transparent" />
          <div className="space-y-6">
            {MILESTONES.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative pr-12"
              >
                <div className="absolute right-0 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card">
                  <div className="h-3 w-3 rounded-full bg-primary-main shadow-lg shadow-primary-main/40" />
                </div>
                <div className="rounded-2xl border border-border bg-card p-5">
                  <div className="mb-1.5 inline-block rounded-full bg-secondary-main/15 px-2.5 py-0.5 text-[10.5px] font-black text-secondary-main">
                    {m.year}
                  </div>
                  <h3 className="mb-1 text-[15px] font-black text-foreground">{m.title}</h3>
                  <p className="text-[12.5px] leading-relaxed text-muted-foreground">{m.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="mb-8 text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5">
            <Users size={13} className="text-primary-main" />
            <span className="text-[11.5px] font-bold text-muted-foreground">تیم ما</span>
          </div>
          <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">
            آدم‌هایی که این محصول را می‌سازند
          </h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TEAM.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="rounded-2xl border border-border bg-card p-6 text-center"
            >
              <div
                className={`mx-auto mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br ${p.color} text-[18px] font-black text-white shadow-lg`}
              >
                {p.initials}
              </div>
              <h3 className="text-[14px] font-black text-foreground">{p.name}</h3>
              <p className="mt-1 text-[11.5px] text-muted-foreground">{p.role}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-6 pb-24">
        <div className="rounded-3xl border border-primary-main/30 bg-gradient-to-br from-primary-main/10 via-card to-secondary-main/10 p-10 text-center">
          <h2 className="mb-3 text-2xl font-black text-foreground sm:text-3xl">
            می‌خواهی همراه ما باشی؟
          </h2>
          <p className="mx-auto mb-6 max-w-md text-[14px] text-muted-foreground">
            چه کاربر باشی، چه همکار آینده — خوشحال می‌شویم بشنویم.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 rounded-xl bg-primary-main px-6 py-3 text-[14px] font-black text-white shadow-lg shadow-primary-main/30 hover:bg-primary-400"
          >
            تماس با ما
            <ArrowLeft size={15} />
          </Link>
        </div>
      </section>
    </SiteShell>
  );
}
